import ObjectId from 'bson-objectid';
import { getCalculatedText } from 'core';
import { Api, formatText, getYtId, isUrl, uploadMedia } from 'lib';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  BtnGhost,
  BtnPrimary,
  FooterBar,
  ReadyLabel,
  SectionHeader,
  SectionLabel,
  SectionTag,
  Textarea,
  UploadMedia
} from '../../components';
import modernist from '../../modernistTheme';
import { ShareWithPicker, TagFriendsPicker } from './components';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.1fr;
`;

const MediaCol = styled.div`
  padding: 28px 32px 32px 32px;
`;

const DetailsCol = styled.div`
  padding: 28px 32px 32px 32px;
  border-left: 2px solid ${modernist.text};
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const LinkCard = styled.div`
  margin-top: 20px;
  border: 2px solid ${modernist.text};
`;

const MediaFrame = styled.div`
  height: 236px;
  background: ${modernist.neutral200};
  display: grid;
  place-items: center;
  overflow: hidden;
`;

const MediaImg = styled.img`
  width: 100%;
  height: 236px;
  object-fit: cover;
  display: block;
`;

const StyledReactPlayer = styled(ReactPlayer)`
  width: 100% !important;
  height: 236px !important;

  video {
    object-fit: cover;
  }
`;

const MediaFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-top: 2px solid ${modernist.text};
`;

const FileName = styled.div`
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileMeta = styled.div`
  font-size: 12px;
  color: ${modernist.neutral600};
`;

const SmallGhostBtn = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 700;
  font-family: ${modernist.fontBody};
  color: ${(props) => (props.$danger ? modernist.accentDark : modernist.neutral700)};
  cursor: pointer;
`;

const LinkInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  appearance: none;
  background: transparent;
  border: 2px solid ${modernist.neutral400};
  outline: none;
  padding: 12px;
  font-family: ${modernist.fontBody};
  font-size: 15px;
  color: ${modernist.text};

  &:focus {
    border-color: ${modernist.accent};
  }
`;

let timer;
const PostDetails = ({ data, setData, handleClose }) => {
  const [linkError, setLinkError] = useState(''),
    user = useSelector((state) => state.user),
    dispatch = useDispatch(),
    { t } = useTranslation(),
    video = data.media?.find((x) => x.file?.type?.includes('video')),
    hasPicked = !!data.media?.length || !!video || typeof data.link === 'string',
    firstMedia =
      (data.media?.length && data.media[0].preview) ||
      data.metaData?.image ||
      (data.youtubeId && `https://img.youtube.com/vi/${data.youtubeId}/0.jpg`);

  const privacyOptions = [
    { value: t('settings.anyone'), option: 'anyone' },
    { value: t('settings.friendsOnly'), option: 'friends' },
    { value: t('settings.noOne'), option: 'noOne' }
  ];

  useEffect(() => {
    setData((data) => ({
      ...data,
      privacy: privacyOptions[0],
      caption: '',
      tagged: []
    }));

    data.link && handleLinkChange(data.link, true);
  }, []);

  async function handleLinkChange(val, isInitial) {
    clearTimeout(timer);
    handleChange({ link: val });

    timer = setTimeout(async () => {
      setLinkError('');

      if (!val) {
        handleChange({ metaData: null });
        handleChange({ youtubeId: '' });
        return;
      }

      if (!isUrl) return setLinkError(t('add.correctYoutubeLink'));
      if (data.link && val.includes(data.link) && !isInitial) return;

      const isYoutube = getYtId(val);

      if (isYoutube) {
        handleChange({ metaData: null });
        handleChange({ youtubeId: isYoutube });
        setLinkError('');
        return;
      }

      const fetchedMetaData = await Api.post('/posts/getMetaData', { link: val });

      if (fetchedMetaData.err) {
        dispatch({ type: 'HANDLE_RES', payload: { err: fetchedMetaData.err } });
        return;
      }

      if (!fetchedMetaData) return;

      handleChange({ metaData: fetchedMetaData });
      handleChange({ youtubeId: '' });
    }, 300);
  }

  async function submit() {
    dispatch({ type: 'LOADING_BUTTON', payload: 'navigator' });

    const formatted = formatText(data.caption);
    const tags = formatted.filter((x) => x.type === 'tag').map((x) => x.text),
      mentioned = formatted.filter((x) => x.type === 'mention').map((x) => x.text);

    let postData = {};
    let res;

    if (data.metaData) {
      postData = {
        tagged: data.tagged?.map((x) => x._id),
        shareWith: data.privacy?.option,
        caption: data.caption,
        tags,
        mentioned,
        aspectRatio: 1,
        metaData: data.metaData
      };
      res = await Api.post('/posts/create', postData);
    }

    if (!data.youtubeId && !video && !data.metaData) {
      const uploadedMedia = data.media?.length
        ? await Promise.all(data.media?.map((x, i) => uploadMedia(x, i)))
        : null;

      postData = {
        media: uploadedMedia || [],
        tagged: data.tagged?.map((x) => x._id),
        shareWith: data.privacy?.option,
        caption: data.caption,
        aspectRatio: 1,
        tags,
        mentioned
      };
      res = await Api.post('/posts/create', postData);
    }

    if (data.youtubeId && !video) {
      res = await Api.post('/posts/create', {
        youtubeId: data.youtubeId,
        caption: data.caption,
        tags,
        tagged: data.tagged?.map((x) => x._id),
        shareWith: data.privacy?.option
      });
    }

    if (video && !data.youtubeId) {
      postData = new FormData();
      const fileId = ObjectId().toString();

      postData.append('caption', data.caption);
      postData.append('tags', JSON.stringify(tags));
      postData.append('tagged', JSON.stringify(data.tagged?.map((x) => x._id)));
      postData.append('shareWith', data.privacy?.option);
      postData.append('mentioned', JSON.stringify(mentioned));

      const file = video.file;
      const fileType = file.type.split('/')[1];
      const fileName = `${fileId}-1.${fileType}`;

      postData.append('fileName', fileName);
      postData.append('video', file, fileName);

      res = await Api.post('/posts/create', postData);
    }

    if (!res) {
      dispatch({ type: 'LOADING_BUTTON', payload: null });
      return;
    }

    dispatch({
      type: 'ADD_POST',
      payload: { ...res, owner: user, calculatedText: await getCalculatedText(res.caption) }
    });

    dispatch({ type: 'LOADING_BUTTON', payload: null });
    handleClose();
  }

  function handleChange(opt) {
    setData((data) => ({ ...data, ...opt }));
  }

  function clearMedia() {
    handleChange({ media: undefined });
  }

  const ready =
    (!!data.media?.length || !!video || !!data.caption?.trim().length || !!data.metaData) &&
    (!linkError || !data.link);

  return (
    <React.Fragment>
      <Grid>
        <MediaCol>
          <SectionHeader>
            <SectionLabel>01 — {t('add.uploadMedia')}</SectionLabel>
            <SectionTag>{t('common.optional', 'Optional')}</SectionTag>
          </SectionHeader>

          {!!firstMedia && !video && (
            <LinkCard>
              <MediaFrame>
                <MediaImg src={firstMedia} alt="" />
              </MediaFrame>
              <MediaFoot>
                <div style={{ minWidth: 0 }}>
                  <FileName>{data.media?.[0]?.file?.name || t('add.linkToOriginalPost')}</FileName>
                  <FileMeta>
                    {data.media?.[0]?.file &&
                      `${(data.media[0].file.size / 1048576).toFixed(1)} MB`}
                  </FileMeta>
                </div>
                {!!data.media?.length && (
                  <SmallGhostBtn type="button" $danger onClick={clearMedia}>
                    {t('common.remove')}
                  </SmallGhostBtn>
                )}
              </MediaFoot>
            </LinkCard>
          )}

          {!!video && (
            <LinkCard>
              <MediaFrame>
                <StyledReactPlayer url={video.uri} controls width="100%" height="236px" />
              </MediaFrame>
              <MediaFoot>
                <div style={{ minWidth: 0 }}>
                  <FileName>{video.file?.name}</FileName>
                  <FileMeta>{(video.file.size / 1048576).toFixed(1)} MB</FileMeta>
                </div>
                <SmallGhostBtn type="button" $danger onClick={clearMedia}>
                  {t('common.remove')}
                </SmallGhostBtn>
              </MediaFoot>
            </LinkCard>
          )}

          {!hasPicked && (
            <div style={{ marginTop: 20 }}>
              <UploadMedia wid="100%" ht="300px" setData={setData} />
            </div>
          )}
        </MediaCol>

        <DetailsCol>
          {typeof data.link === 'string' && (
            <div>
              <SectionHeader>
                <SectionLabel>{t('add.linkToOriginalPost')}</SectionLabel>
              </SectionHeader>
              <div style={{ marginTop: 12 }}>
                <LinkInput
                  value={data.link || ''}
                  onChange={(e) => handleLinkChange(e.target.value)}
                  placeholder={t('add.enterLink')}
                />
                {!!linkError && (
                  <FileMeta style={{ color: modernist.accentDark, marginTop: 6 }}>
                    {linkError}
                  </FileMeta>
                )}
              </div>
            </div>
          )}

          <TagFriendsPicker
            selected={data.tagged || []}
            onChange={(opt) => handleChange({ tagged: opt })}
          />

          <ShareWithPicker
            options={privacyOptions}
            selected={data.privacy}
            onChange={(opt) => handleChange({ privacy: opt })}
          />

          <div>
            <SectionHeader>
              <SectionLabel>04 — {t('add.caption', 'Caption')}</SectionLabel>
              <SectionTag>{data.caption?.length || 0} / 280</SectionTag>
            </SectionHeader>
            <Textarea
              rows={3}
              value={data.caption || ''}
              onChange={(e) => handleChange({ caption: e.target.value })}
              placeholder={t('common.typeYourMessage')}
            />
          </div>
        </DetailsCol>
      </Grid>

      <FooterBar>
        <ReadyLabel>
          {ready
            ? t('add.readyToPublish', 'Ready to publish')
            : t('add.addFileOrCaption', 'Add a file or a caption to continue')}
        </ReadyLabel>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <BtnGhost type="button" onClick={() => setData({})}>
            {t('common.discard', 'Discard')}
          </BtnGhost>
          <BtnPrimary type="button" onClick={submit} disabled={!ready}>
            {t('add.create')}
          </BtnPrimary>
        </div>
      </FooterBar>
    </React.Fragment>
  );
};

export default PostDetails;
